import {
  CheckCircle,
  Info,
  Warning,
  WarningCircle,
  X,
} from "@phosphor-icons/react";
import { motion, useReducedMotion } from "motion/react";
import type { ReactNode } from "react";

import { cn } from "../../lib/cn";
import { motion as motionTokens } from "../../tokens";

export type BannerStatus = "success" | "error" | "warning" | "info";

const styles: Record<BannerStatus, string> = {
  success: "bg-success-surface text-success-text",
  error: "bg-error-surface text-error-text",
  warning: "bg-warning-surface text-warning-text",
  info: "bg-info-surface text-info-text",
};

const iconColor: Record<BannerStatus, string> = {
  success: "text-success",
  error: "text-error",
  warning: "text-warning",
  info: "text-info",
};

const icons: Record<BannerStatus, typeof Info> = {
  success: CheckCircle,
  error: WarningCircle,
  warning: Warning,
  info: Info,
};

export type BannerProps = {
  status: BannerStatus;
  onDismiss?: () => void;
  className?: string;
  children: ReactNode;
};

export function Banner({
  status,
  onDismiss,
  className,
  children,
}: BannerProps) {
  const reduce = useReducedMotion();
  const Icon = icons[status];
  const role = status === "error" ? "alert" : "status";
  const cls = cn(
    "flex items-start gap-3 overflow-hidden rounded-lg p-3 text-body",
    styles[status],
    className,
  );

  const inner = (
    <>
      <Icon
        size={20}
        weight="regular"
        className={cn("mt-0.5 shrink-0", iconColor[status])}
        aria-hidden="true"
      />
      <div className="flex-1">{children}</div>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="shrink-0 rounded p-0.5 hover:bg-black/5"
        >
          <X size={20} aria-hidden="true" />
        </button>
      )}
    </>
  );

  if (reduce) {
    return (
      <div role={role} className={cls}>
        {inner}
      </div>
    );
  }

  return (
    <motion.div
      role={role}
      className={cls}
      initial={{ opacity: 0, y: -8, scale: 0.985 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{
        opacity: 0,
        height: 0,
        marginTop: 0,
        marginBottom: 0,
        paddingTop: 0,
        paddingBottom: 0,
      }}
      transition={motionTokens.spring.default}
    >
      {inner}
    </motion.div>
  );
}
